import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { CloseIcon } from "../../assets/icons/closeIcon";
import { PicInPicIcon } from "../../assets/icons/picInPicIcon";
import { PicOutPicIcon } from "../../assets/icons/picOutPic";
import { TextAreaResizeIcon } from "../../assets/icons/textAreaResizeIcon";
import { designSystemTokenValues, designSystemVars as vars } from "../tokens";
import { Modal } from "./Dialog";
import { FloatingPanelLauncher } from "./FloatingPanelControls";
import {
  FloatingPanelCloseButton,
  FloatingPanelContent,
  FloatingPanelHeader,
  FloatingPanelHeaderActions,
  FloatingPanelSurface,
  FloatingPanelTitle,
} from "./FloatingPanel";

const appear = keyframes`from { opacity: 0; } to { opacity: 1; }`;

type Direction = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";
type Geometry = { left: number; top: number; width: number; height: number };
type Mode = "docked" | "undocked";

interface ResizableFloatingPanelProps {
  title: string;
  controlLabel?: string;
  isOpen?: boolean;
  onClose: () => void;
  onReopen?: () => void;
  isMinimized?: boolean;
  onMinimize?: () => void;
  launcherDisabled?: boolean;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
  compactActions?: boolean;
  bottomClearance?: number;
  centered?: boolean;
  panelRef?: React.RefObject<HTMLDivElement | null>;
  modal?: boolean;
  status?: React.ReactNode;
  actions?: React.ReactNode;
  onResize?: (width: number) => void;
  presentation?: "floating" | "generation";
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const directions: Direction[] = ["n", "s", "e", "w", "ne", "nw", "se", "sw"];
const padding = 16;
const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, Math.max(min, max)));

const Panel = styled(FloatingPanelSurface)`
  box-sizing: border-box;
  animation: ${appear} ${vars.motion.normal} ${vars.motion.easing};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const GenerationModal = styled(Modal)<{
  $geometry: Geometry;
  $resized: boolean;
}>`
  position: fixed;
  width: ${({ $geometry, $resized }) =>
    $resized ? `${$geometry.width}px` : vars.layout.taskModalWidth};
  height: ${({ $geometry, $resized }) =>
    $resized ? `${$geometry.height}px` : "fit-content"};
  left: ${({ $geometry }) => `${$geometry.left}px`};
  top: ${({ $geometry, $resized }) =>
    $resized ? `${$geometry.top}px` : "50%"};
  transform: ${({ $resized }) => ($resized ? "none" : "translateY(-50%)")};

  > header {
    touch-action: none;
    user-select: none;
    cursor: grab;
  }
`;

const Header = styled(FloatingPanelHeader)`
  flex-shrink: 0;
  gap: ${vars.spacing.sm};
  margin-bottom: 0;
  user-select: none;
  touch-action: none;

  ${FloatingPanelTitle} {
    flex-wrap: wrap;
    min-width: 0;
    margin: ${vars.spacing.sm} 0;
  }

  ${FloatingPanelHeaderActions} {
    gap: ${vars.spacing.xs};
  }
`;

const Handle = styled.div<{ $direction: Direction }>`
  position: absolute;
  z-index: 2;
  touch-action: none;
  svg {
    position: absolute;
    right: ${vars.spacing.sm};
    bottom: ${vars.spacing.sm};
    pointer-events: none;
  }
  ${({ $direction: d }) => {
    const corner = d.length === 2;
    return {
      top: d.includes("n") ? -5 : d === "e" || d === "w" ? 14 : undefined,
      bottom: d.includes("s") ? -5 : d === "e" || d === "w" ? 14 : undefined,
      left: d.includes("w") ? -5 : d === "n" || d === "s" ? 14 : undefined,
      right: d.includes("e") ? -5 : d === "n" || d === "s" ? 14 : undefined,
      width: corner ? 14 : d === "e" || d === "w" ? 10 : undefined,
      height: corner ? 14 : d === "n" || d === "s" ? 10 : undefined,
      cursor: corner
        ? d === "ne" || d === "sw"
          ? "nesw-resize"
          : "nwse-resize"
        : d === "n" || d === "s"
          ? "ns-resize"
          : "ew-resize",
    };
  }}

  &:focus-visible {
    outline: none;
    box-shadow: ${vars.shadow.focus};
  }
`;

export const ResizableFloatingPanel: React.FC<ResizableFloatingPanelProps> = ({
  title,
  controlLabel = title,
  isOpen = true,
  onClose,
  onReopen,
  isMinimized = false,
  onMinimize,
  launcherDisabled = false,
  isExpanded = false,
  onToggleExpanded,
  compactActions = false,
  bottomClearance: providedBottomClearance,
  centered = false,
  panelRef,
  modal = false,
  status,
  actions,
  onResize,
  presentation = "floating",
  footer,
  children,
}) => {
  const ownRef = useRef<HTMLDivElement>(null);
  const ref = panelRef ?? ownRef;
  const titleId = useId();
  const isGeneration = presentation === "generation";
  const bottomClearance =
    providedBottomClearance ??
    (isGeneration
      ? padding + parseFloat(designSystemTokenValues.spacing.px72) / 2
      : padding);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(isOpen);
  const focusOnReopen = useRef(false);
  const hasOpened = useRef(isOpen);
  if (isOpen) hasOpened.current = true;
  const mode: Mode = isExpanded ? "undocked" : "docked";
  const [geometries, setGeometries] = useState<Partial<Record<Mode, Geometry>>>(
    {},
  );
  const [viewport, setViewport] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
    header: 120,
  }));
  const [gesture, setGesture] = useState<{
    direction: Direction | "drag";
    start: Geometry;
    x: number;
    y: number;
  } | null>(null);
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;

  useEffect(() => {
    if (!isOpen && wasOpen.current) {
      setGesture(null);
      launcherRef.current?.focus();
    } else if (isOpen && focusOnReopen.current) {
      ref.current?.querySelector<HTMLButtonElement>("button")?.focus();
      focusOnReopen.current = false;
    }
    wasOpen.current = isOpen;
  }, [isOpen, ref]);

  useEffect(() => {
    const measure = () =>
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
        header: Math.max(
          padding,
          (document
            .querySelector<HTMLElement>('[role="banner"]')
            ?.getBoundingClientRect().bottom ?? 112) + 8,
        ),
      });
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const maxWidth = Math.max(1, viewport.width - padding * 2);
  const maxHeight = Math.max(
    1,
    viewport.height - viewport.header - bottomClearance,
  );
  const bound = useCallback(
    (g: Geometry): Geometry => {
      const width = clamp(
        g.width,
        Math.min(300, maxWidth),
        Math.min(940, maxWidth),
      );
      const height = clamp(
        g.height,
        Math.min(220, maxHeight),
        Math.min(880, maxHeight),
      );
      return {
        width,
        height,
        left: clamp(g.left, padding, viewport.width - width - padding),
        top: clamp(
          g.top,
          viewport.header,
          viewport.height - height - bottomClearance,
        ),
      };
    },
    [
      maxWidth,
      maxHeight,
      viewport.width,
      viewport.height,
      viewport.header,
      bottomClearance,
    ],
  );
  const width = Math.min(
    isGeneration
      ? parseFloat(designSystemTokenValues.layout.taskModalWidth)
      : isExpanded
        ? 560
        : 350,
    maxWidth,
  );
  const height = Math.min(
    isExpanded ? (compactActions ? 310 : 342) : compactActions ? 531 : 589,
    maxHeight,
  );
  const geometry = bound(
    geometries[mode] ?? {
      width,
      height,
      left: isExpanded
        ? (viewport.width - width) / 2
        : viewport.width - width - 20,
      top: isExpanded
        ? centered
          ? (viewport.height - height) / 2
          : viewport.header
        : viewport.height - height - bottomClearance,
    },
  );

  useEffect(() => {
    onResizeRef.current?.(geometry.width - 40);
  }, [geometry.width]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const focused =
        event.target instanceof Element ? event.target : document.activeElement;
      if (
        focused?.closest('[role="dialog"]') &&
        !ref.current?.contains(focused)
      )
        return;
      const openPanels = document.querySelectorAll(
        '[data-floating-panel-open="true"]',
      );
      const targetInside =
        event.target instanceof Node && ref.current?.contains(event.target);
      const activeDrawer = document.querySelector(
        '[data-drawer-variant][data-escape-from-outside="true"]',
      );
      if (!targetInside && activeDrawer) return;
      if (!targetInside && openPanels[openPanels.length - 1] !== ref.current)
        return;
      event.preventDefault();
      (onMinimize ?? onClose)();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, onMinimize, ref]);

  useEffect(() => {
    if (!gesture || !isOpen) return;
    const move = (event: PointerEvent) => {
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      const { start, direction } = gesture;
      let next = { ...start };
      if (direction === "drag") {
        next.left += dx;
        next.top += dy;
      } else {
        if (direction.includes("e")) next.width += dx;
        if (direction.includes("s")) next.height += dy;
        if (direction.includes("w")) next.width -= dx;
        if (direction.includes("n")) next.height -= dy;
        next = bound(next);
        if (direction.includes("w"))
          next.left = start.left + start.width - next.width;
        if (direction.includes("n"))
          next.top = start.top + start.height - next.height;
      }
      setGeometries((previous) => ({ ...previous, [mode]: bound(next) }));
    };
    const end = () => setGesture(null);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
  }, [gesture, isOpen, mode, bound]);

  const startGesture = (
    event: React.PointerEvent,
    direction: Direction | "drag",
  ) => {
    if (event.button !== 0 || (direction === "drag" && !isExpanded)) return;
    if (direction === "drag" && (event.target as HTMLElement).closest("button"))
      return;
    event.preventDefault();
    setGesture({
      direction,
      start: getRenderedGeometry(),
      x: event.clientX,
      y: event.clientY,
    });
  };

  const getRenderedGeometry = () => {
    const rect = isGeneration ? ref.current?.getBoundingClientRect() : null;
    return rect?.width && rect.height
      ? bound({
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        })
      : geometry;
  };

  const resizeHandles = directions.map((direction) => (
    <Handle
      key={direction}
      $direction={direction}
      role="separator"
      aria-label={`Resize ${controlLabel} ${direction}`}
      aria-orientation={
        direction === "n" || direction === "s" ? "horizontal" : "vertical"
      }
      tabIndex={0}
      onPointerDown={(event) => startGesture(event, direction)}
      onKeyDown={(event) => {
        const delta = event.shiftKey ? 40 : 10;
        const horizontal =
          event.key === "ArrowRight"
            ? delta
            : event.key === "ArrowLeft"
              ? -delta
              : 0;
        const vertical =
          event.key === "ArrowDown"
            ? delta
            : event.key === "ArrowUp"
              ? -delta
              : 0;
        if (!horizontal && !vertical) return;
        event.preventDefault();
        const current = getRenderedGeometry();
        const next = bound({
          ...current,
          width:
            current.width +
            (direction.includes("e")
              ? horizontal
              : direction.includes("w")
                ? -horizontal
                : 0),
          height:
            current.height +
            (direction.includes("s")
              ? vertical
              : direction.includes("n")
                ? -vertical
                : 0),
        });
        if (direction.includes("w"))
          next.left = current.left + current.width - next.width;
        if (direction.includes("n"))
          next.top = current.top + current.height - next.height;
        setGeometries((previous) => ({
          ...previous,
          [mode]: bound(next),
        }));
      }}
    >
      {direction === "se" && (
        <span aria-hidden="true">
          <TextAreaResizeIcon />
        </span>
      )}
    </Handle>
  ));

  if (!hasOpened.current && !isMinimized) return null;

  return (
    <>
      {!isOpen && isMinimized && onReopen && (
        <FloatingPanelLauncher
          ref={launcherRef}
          aria-label={`Reopen ${controlLabel}`}
          title={`Reopen ${controlLabel}`}
          disabled={launcherDisabled}
          onClick={() => {
            focusOnReopen.current = true;
            onReopen();
          }}
        ></FloatingPanelLauncher>
      )}
      <div hidden={!isOpen} style={{ display: isOpen ? "contents" : "none" }}>
        {isGeneration ? (
          <div
            onPointerDown={(event) => {
              const target = event.target as Element;
              if (
                !target.closest('[role="separator"]') &&
                target.closest("header") ===
                  ref.current?.querySelector("header")
              ) {
                startGesture(event, "drag");
              }
            }}
          >
            <GenerationModal
              open={isOpen}
              keepMounted
              title={title}
              closeLabel={`${onMinimize ? "Minimize" : "Close"} ${controlLabel}`}
              onClose={onMinimize ?? onClose}
              intent="generation"
              size="task"
              surfaceRef={ref}
              headerActions={resizeHandles}
              footer={footer}
              $geometry={geometry}
              $resized={!!geometries[mode]}
            >
              {children}
            </GenerationModal>
          </div>
        ) : (
          <Panel
            ref={ref}
            role={modal ? "dialog" : "region"}
            aria-modal={modal ? true : undefined}
            aria-labelledby={titleId}
            tabIndex={modal ? -1 : undefined}
            data-floating-panel-open={isOpen}
            $isExpanded={isExpanded}
            style={{
              width: geometry.width,
              height: geometry.height,
              left: geometry.left,
              top: geometry.top,
              right: "auto",
              bottom: "auto",
              transform: "none",
            }}
          >
            {resizeHandles}
            <FloatingPanelContent $isExpanded={isExpanded}>
              <Header
                onPointerDown={(event) => startGesture(event, "drag")}
                style={{
                  cursor: isExpanded
                    ? gesture
                      ? "grabbing"
                      : "grab"
                    : "default",
                }}
              >
                <FloatingPanelTitle id={titleId}>
                  {title}
                  {status}
                </FloatingPanelTitle>
                <FloatingPanelHeaderActions>
                  {actions}
                  {onToggleExpanded && (
                    <FloatingPanelCloseButton
                      aria-label={`${isExpanded ? "Dock" : "Undock"} ${controlLabel}`}
                      onClick={() => {
                        setGesture(null);
                        onToggleExpanded();
                      }}
                    >
                      {isExpanded ? <PicOutPicIcon /> : <PicInPicIcon />}
                    </FloatingPanelCloseButton>
                  )}
                  <FloatingPanelCloseButton
                    aria-label={`${onMinimize ? "Minimize" : "Close"} ${controlLabel}`}
                    title={`${onMinimize ? "Minimize" : "Close"} ${controlLabel}`}
                    onClick={onMinimize ?? onClose}
                  >
                    <CloseIcon />
                  </FloatingPanelCloseButton>
                </FloatingPanelHeaderActions>
              </Header>
              {children}
            </FloatingPanelContent>
          </Panel>
        )}
      </div>
    </>
  );
};
